import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Field } from '@kontentino/ui';
import {
  AssetTypeFilter,
  UploadedByFilter,
} from 'app/modules/mediaLibraryPage/types/AssetFilters';
import {
  SortDirection,
  SortField,
  SortOption,
} from 'app/modules/mediaLibraryPage/types/SortOption';

const TYPE_OPTIONS: { value: AssetTypeFilter; labelKey: string }[] = [
  { value: 'images', labelKey: 'mediaLibrary.page.toolbar.type.image' },
  { value: 'videos', labelKey: 'mediaLibrary.page.toolbar.type.video' },
  { value: 'gifs', labelKey: 'mediaLibrary.page.toolbar.type.gif' },
  { value: 'documents', labelKey: 'mediaLibrary.page.toolbar.type.document' },
];

const UPLOADED_BY_OPTIONS: { value: UploadedByFilter; labelKey: string }[] = [
  { value: 'me', labelKey: 'mediaLibrary.page.toolbar.uploadedBy.me' },
  { value: 'anyone', labelKey: 'mediaLibrary.page.toolbar.uploadedBy.anyone' },
];

const SORT_FIELD_OPTIONS: { value: SortField; labelKey: string }[] = [
  { value: 'created_at', labelKey: 'mediaLibrary.page.toolbar.sort.newest' },
  { value: 'name', labelKey: 'mediaLibrary.page.toolbar.sort.name' },
  { value: 'file_size', labelKey: 'mediaLibrary.page.toolbar.sort.fileSize' },
];

const SORT_DIRECTION_OPTIONS: { value: SortDirection; labelKey: string }[] = [
  { value: 'desc', labelKey: 'mediaLibrary.page.toolbar.sort.descending' },
  { value: 'asc', labelKey: 'mediaLibrary.page.toolbar.sort.ascending' },
];

interface Props {
  type: AssetTypeFilter | undefined;
  uploadedBy: UploadedByFilter | undefined;
  sort: SortOption;
  onChangeType(next: AssetTypeFilter | undefined): void;
  onChangeUploadedBy(next: UploadedByFilter | undefined): void;
  onChangeSort(field: SortField, direction?: SortDirection): void;
}

const FilterSection: FC<{ title: string; children: ReactNode }> = ({
  title,
  children,
}) => (
  <div className="tw-mt-4">
    <Field.Label required className="tw-mb-2">
      {title}
    </Field.Label>
    <div className="tw-flex tw-flex-col tw-gap-1">{children}</div>
  </div>
);

const CheckRow: FC<{
  label: string;
  isChecked: boolean;
  onToggle(): void;
  dataCy?: string;
}> = ({ label, isChecked, onToggle, dataCy }) => (
  <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-py-1 tw-text-sm tw-text-grayscale-180">
    <Checkbox
      size="small"
      checked={isChecked}
      onChange={onToggle}
      data-cy={dataCy}
    />
    <span className="tw-truncate">{label}</span>
  </label>
);

const MediaLibraryPickerFilters: FC<Props> = ({
  type,
  uploadedBy,
  sort,
  onChangeType,
  onChangeUploadedBy,
  onChangeSort,
}) => {
  const { t } = useTranslation();

  return (
    <div data-cy="media-library-picker_filters">
      <FilterSection title={t('mediaLibrary.page.toolbar.type.all')}>
        {TYPE_OPTIONS.map((option) => (
          <CheckRow
            key={`type-${option.value}`}
            dataCy={`media-library-picker_filters-type-${option.value}`}
            label={t(option.labelKey)}
            isChecked={type === option.value}
            onToggle={() =>
              onChangeType(type === option.value ? undefined : option.value)
            }
          />
        ))}
      </FilterSection>

      <FilterSection title={t('mediaLibrary.page.toolbar.uploadedBy.all')}>
        {UPLOADED_BY_OPTIONS.map((option) => (
          <CheckRow
            key={`uploaded-by-${option.value}`}
            dataCy={`media-library-picker_filters-uploaded-by-${option.value}`}
            label={t(option.labelKey)}
            isChecked={uploadedBy === option.value}
            onToggle={() =>
              onChangeUploadedBy(
                uploadedBy === option.value ? undefined : option.value,
              )
            }
          />
        ))}
      </FilterSection>

      <FilterSection title={t('mediaLibrary.page.toolbar.sortBy')}>
        {SORT_FIELD_OPTIONS.map((option) => (
          <CheckRow
            key={`sort-field-${option.value}`}
            dataCy={`media-library-picker_filters-sort-${option.value}`}
            label={t(option.labelKey)}
            isChecked={sort.by === option.value}
            onToggle={() => onChangeSort(option.value, sort.direction)}
          />
        ))}
      </FilterSection>

      <FilterSection title={t('mediaLibrary.page.toolbar.order')}>
        {SORT_DIRECTION_OPTIONS.map((option) => (
          <CheckRow
            key={`sort-direction-${option.value}`}
            dataCy={`media-library-picker_filters-sort-direction-${option.value}`}
            label={t(option.labelKey)}
            isChecked={sort.direction === option.value}
            onToggle={() => onChangeSort(sort.by, option.value)}
          />
        ))}
      </FilterSection>
    </div>
  );
};

export default MediaLibraryPickerFilters;
